import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 9: "Sorting Challenge" (Master Spec Section 5), campaign
 * levels 81-90. Teaches: compare sorting strategies, choose an algorithm, comparisons, swaps, shifts, operations, efficiency reasoning.
 * Algorithm(s) used in this world: bubble-sort, insertion-sort, selection-sort.
 */
export const world09SortingChallengeLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-081",
    world: 9,
    levelInWorld: 1,
    globalLevelNumber: 81,
    algorithm: "bubble-sort",
    values: [9, 3, 14, 6, 11, 2, 8],
    sortDirection: "ascending",
    learningObjectives: [
      "Recall Bubble Sort's adjacent-comparison-and-swap process on a fresh dataset.",
      "Count comparisons and swaps as you go, to compare against the other strategies in this world.",
    ],
    notes: "Bubble Sort, ascending -- the first of three strategies compared in this world.",
  }),
  createCampaignLevel({
    id: "campaign-082",
    world: 9,
    levelInWorld: 2,
    globalLevelNumber: 82,
    algorithm: "selection-sort",
    values: [15, 4, 22, 7, 18, 1, 10],
    sortDirection: "ascending",
    learningObjectives: [
      "Recall Selection Sort's scan-and-select process on a similarly sized dataset.",
      "Compare the number of scans this needs against Bubble Sort's passes.",
    ],
    notes: "Selection Sort, ascending, same size as level 81 -- a direct strategy comparison.",
  }),
  createCampaignLevel({
    id: "campaign-083",
    world: 9,
    levelInWorld: 3,
    globalLevelNumber: 83,
    algorithm: "insertion-sort",
    values: [12, 5, 19, 3, 16, 8, 2],
    sortDirection: "ascending",
    learningObjectives: [
      "Recall Insertion Sort's grow-the-sorted-region process on a similarly sized dataset.",
      "Complete the three-way comparison: Bubble, Selection, and Insertion on comparable datasets.",
    ],
    notes: "Insertion Sort, ascending, same size as levels 81-82 -- the third strategy compared.",
  }),
  createCampaignLevel({
    id: "campaign-084",
    world: 9,
    levelInWorld: 4,
    globalLevelNumber: 84,
    algorithm: "bubble-sort",
    values: [3, 5, 4, 6, 8, 7, 9, 10],
    sortDirection: "ascending",
    learningObjectives: [
      "See Bubble Sort's early termination pay off on data that starts close to sorted.",
      "Notice how few passes are needed compared to a fully shuffled dataset of the same size.",
    ],
    notes: "Nearly sorted -- Bubble Sort finishes in very few passes thanks to early termination.",
  }),
  createCampaignLevel({
    id: "campaign-085",
    world: 9,
    levelInWorld: 5,
    globalLevelNumber: 85,
    algorithm: "selection-sort",
    values: [3, 5, 4, 6, 8, 7, 9, 10],
    sortDirection: "ascending",
    learningObjectives: [
      "See Selection Sort scan the full unsorted region every round even though the data is nearly sorted.",
      "Directly contrast this with level 84: identical data, but no early-termination benefit here.",
    ],
    notes: "Same nearly-sorted values as level 84 -- Selection Sort still performs a full scan each round.",
  }),
  createCampaignLevel({
    id: "campaign-086",
    world: 9,
    levelInWorld: 6,
    globalLevelNumber: 86,
    algorithm: "insertion-sort",
    values: [3, 5, 4, 6, 8, 7, 9, 10],
    sortDirection: "ascending",
    learningObjectives: [
      "See Insertion Sort need only a couple of small shifts on this nearly-sorted data.",
      "Complete the three-way contrast: compare shift count here to level 84's passes and level 85's scans.",
    ],
    notes: "Same nearly-sorted values as levels 84-85 -- Insertion Sort needs only a couple of small shifts.",
  }),
  createCampaignLevel({
    id: "campaign-087",
    world: 9,
    levelInWorld: 7,
    globalLevelNumber: 87,
    algorithm: "bubble-sort",
    values: [24, 19, 31, 8, 15, 27, 4, 12, 36],
    sortDirection: "descending",
    learningObjectives: [
      "Apply Bubble Sort to a larger, fully shuffled dataset sorted in descending order.",
      "Estimate the pass count before running the sort, then check your prediction.",
    ],
    notes: "Bubble Sort, descending, 9 items -- the larger dataset used across this world's second comparison round.",
  }),
  createCampaignLevel({
    id: "campaign-088",
    world: 9,
    levelInWorld: 8,
    globalLevelNumber: 88,
    algorithm: "selection-sort",
    values: [17, 29, 6, 33, 11, 25, 2, 20, 9],
    sortDirection: "descending",
    learningObjectives: [
      "Apply Selection Sort to a similarly sized, fully shuffled dataset.",
      "Compare the total number of comparisons here against level 87's Bubble Sort pass.",
    ],
    notes: "Selection Sort, descending, same size as level 87 -- another strategy comparison at this larger scale.",
  }),
  createCampaignLevel({
    id: "campaign-089",
    world: 9,
    levelInWorld: 9,
    globalLevelNumber: 89,
    algorithm: "insertion-sort",
    values: [21, 6, 28, 13, 35, 9, 17, 3, 24],
    sortDirection: "descending",
    learningObjectives: [
      "Apply Insertion Sort to a similarly sized, fully shuffled dataset.",
      "Finish the larger-scale three-way comparison: Bubble, Selection, and Insertion at 9 items.",
    ],
    notes: "Insertion Sort, descending, same size as levels 87-88 -- completes the larger-scale comparison.",
  }),
  createCampaignLevel({
    id: "campaign-090",
    world: 9,
    levelInWorld: 10,
    globalLevelNumber: 90,
    algorithm: "insertion-sort",
    values: [14, 3, 27, 9, 20, 6, 17, 31, 11, 2],
    sortDirection: "ascending",
    learningObjectives: [
      "Choose Insertion Sort deliberately for this capstone, reasoning about which strategy fits best.",
      "Consolidate the whole Sorting Challenge: comparisons, swaps, shifts, and operation counts as efficiency signals.",
    ],
    notes: "World 9 capstone -- the largest dataset in the Sorting Challenge, sorted ascending.",
  }),
];
